import { useResizeObserver } from '@mantine/hooks'
import { Masonry, useInfiniteLoader } from 'masonic'
import type { HTMLAttributes, MouseEvent } from 'react'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import FluentOpen20Filled from '~icons/fluent/open-20-filled'
import {
  ArtworkFeedAdUnit,
  insertCampaignAdUnitInGrid,
} from 'src/components/AdUnit/FeedAdUnit'
import Text from 'src/components/ui/Text'
import { useArtworkListPageContext } from 'src/pages/AppPage/ArtworkListPageContext'
import { useMyInfo } from 'src/services/apollo/auth'
import type { ArtworkWithMangaPreviewFragment } from 'src/services/apollo/generated/graphql'
import { MangaType, RefType } from 'src/services/apollo/generated/graphql'
import {
  type CampaignAdEntry,
  useCampaignAds,
} from 'src/services/orpc/campaignAds'
import { getArtworkLink, isAnimatedComic } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import type { ArtworkPreviewCardSimpleProps } from '../Artwork/ArtworkPreviewCardSimple'
import ArtworkPreviewCardSimple from '../Artwork/ArtworkPreviewCardSimple'
import ArtworkTrackedPreviewCard from '../Artwork/ArtworkTrackedPreviewCard'
import type { BaseArtworkItemPreviewCardProps } from '../Artwork/BaseArtworkItemPreviewCard'
import ArtworkDetailDialog from '../ArtworkDetail/ArtworkDetailDialog'
import useArtworkIdFromQueryParams from '../ArtworkDetail/utils'
import type { ArtworkInfiniteScrollContentProps } from './BaseArtworkInfiniteScroll'

export type ArtworkGridMode = 'grid' | 'waterfall'

// Union type for masonry items - either artwork or ad
type MasonryItem =
  | { type: 'artwork'; data: ArtworkWithMangaPreviewFragment }
  | { type: 'ad'; data: CampaignAdEntry }

// Max number of ads to insert at the beginning of the feed
const MAX_FEED_ADS = 4

// Context for passing props to masonic render function
interface MasonryItemContext {
  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']
  showIndex?: boolean
  /** Offset added to each card's index — used when the grid renders a slice
   *  whose first item is not rank 1 (e.g. a podium splits off the top 3). */
  startIndex?: number
  enableTracking?: boolean
  showDislike?: boolean
  showDislikeOverlay?: boolean
  artworkCardProps?: ArtworkPreviewCardSimpleProps
  openArtwork: (
    artwork: ArtworkWithMangaPreviewFragment,
    newWindow?: boolean,
  ) => void
  handleArtworkClick: (
    artwork?: ArtworkWithMangaPreviewFragment,
    index?: number,
  ) => void
  preventDefaultClick: (e: MouseEvent) => void
}

// Context for masonry items
const MasonryContext = React.createContext<MasonryItemContext | null>(null)
const useMasonryContext = () => {
  const ctx = React.useContext(MasonryContext)
  if (!ctx) throw new Error('MasonryContext not found')
  return ctx
}

/** Shared card rendering logic for both grid and masonry modes */
function renderArtworkCard(
  artwork: ArtworkWithMangaPreviewFragment,
  ctx: MasonryItemContext,
  options: {
    index: number
    naturalAspectRatio: boolean
    lastElementRef?: React.RefObject<HTMLDivElement | null> | null
    keyPrefix?: string
  },
): React.ReactElement {
  const {
    trackingArgs,
    showIndex,
    startIndex = 0,
    enableTracking,
    showDislike,
    showDislikeOverlay,
    artworkCardProps,
    openArtwork,
    handleArtworkClick,
    preventDefaultClick,
  } = ctx
  const { index, naturalAspectRatio, lastElementRef } = options
  const displayIndex = startIndex + index

  const cardProps = {
    trackingArgs,
    artwork,
    manga: artwork.manga,
    onClick: preventDefaultClick,
    onArtworkClick: handleArtworkClick,
    showAuthor: true,
    lastElementRef,
    to: getArtworkLink(artwork),
    index: displayIndex,
    showIndex,
    showDislike,
    showDislikeOverlay,
    naturalAspectRatio,
    menu: [
      {
        id: 'open-in-new-window',
        icon: <FluentOpen20Filled className="size-5" />,
        children: <Text i18nKey="action:open-in-new-window" />,
        onAction: () => openArtwork(artwork, true),
      },
    ],
    oauthReturnOpensDetail: true,
    mediaLoading: index < 8 ? ('eager' as const) : ('lazy' as const),
    mediaFetchPriority: index < 4 ? ('high' as const) : ('auto' as const),
    ...artworkCardProps,
  } satisfies ArtworkPreviewCardSimpleProps

  return enableTracking ? (
    <ArtworkTrackedPreviewCard
      key={artwork.id}
      id={artwork.id}
      {...cardProps}
    />
  ) : (
    <ArtworkPreviewCardSimple key={artwork.id} {...cardProps} />
  )
}

// Masonry render function for items (artwork or ad)
function MasonryItemCard({
  data: item,
  index,
}: {
  data: MasonryItem
  index: number
}) {
  const ctx = useMasonryContext()

  if (item.type === 'ad') {
    return <ArtworkFeedAdUnit ad={item.data} mode="masonry" />
  }

  return renderArtworkCard(item.data, ctx, {
    index,
    naturalAspectRatio: true,
  })
}

// Loading skeleton grid
function LoadingSkeletons({ count = 20 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <ArtworkPreviewCardSimple
          key={index}
          loading
          naturalAspectRatio={false}
        />
      ))}
    </>
  )
}

export type ArtworkGridProps = Partial<ArtworkInfiniteScrollContentProps> & {
  className?: string
  showIndex?: boolean
  /** Offset added to each card's index — used when the grid renders a slice
   *  whose first item is not rank 1 (e.g. ranking page splits off the top 3
   *  into a podium and passes `artworks.slice(3)` here with `startIndex={3}`
   *  so the badge shows 4, 5, 6, … instead of restarting at 1). */
  startIndex?: number
  containerProps?: HTMLAttributes<HTMLDivElement>
  /**
   * Modify grid elements after artworks elements have been assembled
   *
   * @usage Inserting ad units to ArtworkGrid
   */
  modifyArtworkElements?: (
    elements: (JSX.Element | null)[],
    opts?: {
      gridItemType: string
    },
  ) => (JSX.Element | null)[]
  artworkCardProps?: ArtworkPreviewCardSimpleProps
  mode?: ArtworkGridMode
}

function ArtworkGrid(props: ArtworkGridProps) {
  const {
    trackingArgs,
    artworks,
    onArtworkClick,
    loading,
    loadMore,
    lastElementRef,
    className,
    showIndex,
    startIndex,
    enableTracking,
    showDislike,
    showDislikeOverlay,
    containerProps,
    modifyArtworkElements = arg => arg,
    artworkCardProps,
    mode = 'grid',
  } = props
  const isWaterfall = mode === 'waterfall'
  const isMobile = useIsMobile()
  const { data: userInfo } = useMyInfo()
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [detailArtwork, setDetailArtwork] =
    useState<ArtworkWithMangaPreviewFragment>()
  const { insertCampaignAdUnit = false } = useArtworkListPageContext()

  const [artworkLikeTrackingArgsForRec, setArtworkLikeTrackingArgsForRec] =
    useState<{
      artworkId: string
      index: number
      currentLikeCount: number
      type: RefType | MangaType
    }>()

  const navigate = useNavigate()
  const preventDefaultClick = useCallback(
    (e: MouseEvent) => e.preventDefault(),
    [],
  )

  const openArtwork = useCallback(
    (artwork: ArtworkWithMangaPreviewFragment, newWindow?: boolean) => {
      if (newWindow) {
        window.open(getArtworkLink(artwork), '_blank')
      } else if (!userInfo) {
        navigate(getArtworkLink(artwork))
      } else {
        setDetailArtwork(artwork)
        setIsDetailOpen(true)
      }
    },
    [navigate, userInfo],
  )

  const handleArtworkClick = useCallback(
    (artwork?: ArtworkWithMangaPreviewFragment, index?: number) => {
      if (!artwork) return
      const manga = artwork.manga
      setArtworkLikeTrackingArgsForRec({
        type: manga
          ? isAnimatedComic(manga)
            ? MangaType.AnimatedComic
            : RefType.Manga
          : RefType.Artwork,
        artworkId: artwork.id,
        currentLikeCount: artwork.likedCount ?? 0,
        index: index ?? 0,
      })
      openArtwork(artwork)
      onArtworkClick?.(artwork)
    },
    [onArtworkClick, openArtwork],
  )

  const onClose = useCallback(() => {
    setArtworkLikeTrackingArgsForRec(undefined) // cleanup tracking args
    setIsDetailOpen(false)
  }, [])

  useArtworkIdFromQueryParams({
    onArtworkOpen: openArtwork,
  })

  const { i18n } = useTranslation()
  const { data: feedCampaign = [], loading: loadingFeedCampaign } =
    useCampaignAds(
      insertCampaignAdUnit
        ? {
            lang: i18n.language,
            category: 'feed',
          }
        : null,
    )

  // Masonic infinite loader for waterfall mode
  const maybeLoadMore = useInfiniteLoader(
    () => {
      loadMore?.()
    },
    {
      isItemLoaded: (index, items) => index < items.length,
      threshold: 16,
    },
  )

  // Container size for masonic
  const [containerRef, rect] = useResizeObserver()
  const containerWidth = rect.width

  // Calculate column settings for masonic
  const { columnCount, columnGutter, columnWidth } = useMemo(() => {
    const count = isMobile ? 2 : 4
    const gutter = isMobile ? 8 : 12
    const width = containerWidth
      ? Math.floor((containerWidth - gutter * (count - 1)) / count)
      : 200

    return {
      columnCount: count,
      columnGutter: gutter,
      columnWidth: width,
    }
  }, [isMobile, containerWidth])
  // Context value shared between masonry and grid modes
  const cardContext = useMemo<MasonryItemContext>(
    () => ({
      trackingArgs,
      showIndex,
      startIndex,
      enableTracking,
      showDislike,
      showDislikeOverlay,
      artworkCardProps,
      openArtwork,
      handleArtworkClick,
      preventDefaultClick,
    }),
    [
      trackingArgs,
      showIndex,
      startIndex,
      enableTracking,
      showDislike,
      showDislikeOverlay,
      artworkCardProps,
      openArtwork,
      handleArtworkClick,
      preventDefaultClick,
    ],
  )

  // Grid mode content
  const gridContent = useMemo(() => {
    if (isWaterfall) return null
    if (!artworks || loadingFeedCampaign) return null

    const elements = artworks.map((artwork, index) => {
      if (!artwork) return null
      const isLastItem = index === artworks.length - 1
      return renderArtworkCard(artwork, cardContext, {
        index,
        naturalAspectRatio: false,
        lastElementRef: isLastItem ? lastElementRef : null,
      })
    })

    if (insertCampaignAdUnit) {
      return insertCampaignAdUnitInGrid(elements, { feedCampaign })
    }
    return modifyArtworkElements(elements)
  }, [
    isWaterfall,
    artworks,
    cardContext,
    modifyArtworkElements,
    feedCampaign,
    insertCampaignAdUnit,
    loadingFeedCampaign,
    lastElementRef,
  ])

  // Items for masonic (filter out null artworks and insert ads)
  const masonryItems = useMemo((): MasonryItem[] => {
    const artworkItems: MasonryItem[] = (artworks ?? [])
      .filter((a): a is ArtworkWithMangaPreviewFragment => a != null)
      .map(artwork => ({ type: 'artwork', data: artwork }))

    // Insert ads at the beginning if enabled
    if (!insertCampaignAdUnit || feedCampaign.length === 0) {
      return artworkItems
    }

    const adsToInsert = feedCampaign
      .slice(0, MAX_FEED_ADS)
      .map((ad): MasonryItem => ({ type: 'ad', data: ad }))

    return [...adsToInsert, ...artworkItems]
  }, [artworks, insertCampaignAdUnit, feedCampaign])

  const isLoading = loading || loadingFeedCampaign

  const artworkDetailDialog = (
    <ArtworkDetailDialog
      open={isDetailOpen}
      onClose={onClose}
      artwork={detailArtwork}
      manga={detailArtwork?.manga}
      trackingArgs={trackingArgs}
      extraLikeTrackingArgsForRec={
        enableTracking ? artworkLikeTrackingArgsForRec : undefined
      }
    />
  )

  if (isWaterfall) {
    return (
      <div
        ref={containerRef}
        className={cx('w-full', className)}
        {...containerProps}
      >
        {containerWidth > 0 && masonryItems.length > 0 && (
          <MasonryContext.Provider value={cardContext}>
            <Masonry
              items={masonryItems}
              columnCount={columnCount}
              columnGutter={columnGutter}
              columnWidth={columnWidth}
              overscanBy={2}
              render={MasonryItemCard}
              onRender={maybeLoadMore}
              itemKey={item =>
                item.type === 'ad' ? `ad-${item.data.trackingId}` : item.data.id
              }
            />
          </MasonryContext.Provider>
        )}
        {isLoading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3 mt-2">
            <LoadingSkeletons />
          </div>
        )}
        {artworkDetailDialog}
      </div>
    )
  }

  return (
    <div
      className={cx(
        'w-full grid content-start grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3',
        className,
      )}
      {...containerProps}
    >
      {gridContent}
      {isLoading && <LoadingSkeletons />}
      {artworkDetailDialog}
    </div>
  )
}

export default ArtworkGrid
